import { Task, TaskStatus, User } from '../types';

export type ExportFormat = 'csv' | 'json';
export type TaskStatusFilter = 'all' | 'approvedOrInReview' | 'approved';
export type ExportType = 'all' | 'answers';

/**
 * Slim row for the "Only Answers" export. `submittedAt` is used as the
 * canonical date — it's when the expert turned in the answer; tasks still
 * in `available`/`claimed` have no submission and show as null.
 */
export interface TaskAnswer {
  id: string;
  status: TaskStatus;
  expertId: string | null;
  submittedAt: Date | null;
  result: unknown;
}

export function filterTasksByStatus(tasks: Task[], filter: TaskStatusFilter): Task[] {
  switch (filter) {
    case 'all':
      return tasks;
    case 'approvedOrInReview':
      return tasks.filter((t) => t.status === 'approved' || t.status === 'in_review');
    case 'approved':
      return tasks.filter((t) => t.status === 'approved');
  }
}

export function toTaskAnswers(tasks: Task[]): TaskAnswer[] {
  return tasks.map((t) => ({
    id: t.id,
    status: t.status,
    expertId: t.expertId,
    submittedAt: t.submittedAt,
    result: t.result,
  }));
}

/**
 * Apply a task type's public result shape before flattening rows for CSV.
 * JSON callers can pass the reshaper to `tasksToJson` directly, but CSV has
 * no callback hook because it flattens the values first.
 */
export function reshapeTaskRowsForExport<T extends { result: unknown }>(
  rows: readonly T[],
  reshapeResult?: (result: unknown) => unknown,
): T[] {
  if (!reshapeResult) return [...rows];
  return rows.map((row) => ({
    ...row,
    result: row.result == null ? row.result : reshapeResult(row.result),
  }));
}

/**
 * Overrides each task's `expertId` with the current value from the owner's
 * user doc. The field on a task is only written at claim time, so tasks
 * claimed before the owner's Aligned account was linked carry a stale null.
 * The users collection is authoritative.
 */
export function resolveTaskExpertIds(
  tasks: Task[],
  usersByUid: Map<string, User>,
): Task[] {
  return tasks.map((task) => {
    if (!task.ownerId) return task;
    const expertId = usersByUid.get(task.ownerId)?.expertId ?? null;
    return { ...task, expertId };
  });
}

/**
 * Top-level row keys keep their order (so Task fields follow the canonical
 * `Task` interface shape from `mapTask`); every nested object has its keys
 * sorted alphabetically so `payload` / `result` look identical across entries
 * regardless of the order Firestore returned the fields in.
 *
 * Task types can opt out of alphabetization for their `result` field by
 * passing `reshapeResult`; the returned shape is then taken AS-IS so the
 * module controls the key order (used e.g. by gdm-rubrics so its reviewer
 * download and the bulk export emit the same JSON).
 */
export function tasksToJson<T>(
  rows: T[],
  options?: { reshapeResult?: (result: unknown) => unknown },
): string {
  const reshapeResult = options?.reshapeResult;
  return JSON.stringify(
    rows.map((row) => canonicalizeRow(row, reshapeResult)),
    null,
    2,
  );
}

function canonicalizeRow(
  row: unknown,
  reshapeResult?: (result: unknown) => unknown,
): unknown {
  if (row === null || typeof row !== 'object' || Array.isArray(row) || row instanceof Date) {
    return sortKeysDeep(row);
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row as Record<string, unknown>)) {
    if (key === 'result' && reshapeResult && value != null) {
      out[key] = reshapeResult(value);
    } else {
      out[key] = sortKeysDeep(value);
    }
  }
  return out;
}

function sortKeysDeep(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (value instanceof Date) return value;
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (typeof value !== 'object') return value;
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const out: Record<string, unknown> = {};
  for (const [k, v] of entries) out[k] = sortKeysDeep(v);
  return out;
}

/**
 * Flat CSV of tasks. Nested `payload` / `result` objects are flattened into
 * dotted-path columns (`payload.foo.bar`), arrays of primitives are joined
 * with `|`, arrays of objects expand to indexed paths (`result.items.0.text`).
 * The column set is the union of every flattened key seen across the batch;
 * missing cells are empty strings. Row order follows input order.
 */
export function tasksToCsv<T>(rows: T[]): string {
  const flatRows = rows.map((r) => flatten(r));
  const columns = collectColumns(flatRows);
  const header = columns.map(csvEscape).join(',');
  const body = flatRows.map((row) =>
    columns.map((col) => csvEscape(row[col] ?? '')).join(','),
  );
  return [header, ...body].join('\n');
}

function collectColumns(rows: Array<Record<string, string>>): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        ordered.push(key);
      }
    }
  }
  return ordered;
}

function flatten(
  value: unknown,
  prefix = '',
  out: Record<string, string> = {},
): Record<string, string> {
  if (value === null || value === undefined) {
    if (prefix) out[prefix] = '';
    return out;
  }
  if (value instanceof Date) {
    out[prefix] = value.toISOString();
    return out;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      out[prefix] = '';
      return out;
    }
    const allPrimitive = value.every(
      (v) => v === null || typeof v !== 'object' || v instanceof Date,
    );
    if (allPrimitive) {
      out[prefix] = value
        .map((v) => (v instanceof Date ? v.toISOString() : v == null ? '' : String(v)))
        .join('|');
      return out;
    }
    value.forEach((item, i) => flatten(item, prefix ? `${prefix}.${i}` : String(i), out));
    return out;
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) {
      if (prefix) out[prefix] = '';
      return out;
    }
    for (const [k, v] of entries) {
      flatten(v, prefix ? `${prefix}.${k}` : k, out);
    }
    return out;
  }
  out[prefix] = String(value);
  return out;
}

function csvEscape(value: string): string {
  const needsQuote = /[",\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuote ? `"${escaped}"` : escaped;
}

export function downloadBlob(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportFilename(
  campaignId: string,
  format: ExportFormat,
  options?: { taskTypeId?: string | null; now?: Date },
): string {
  const now = options?.now ?? new Date();
  const stamp = now.toISOString().slice(0, 10);
  const safeId = campaignId.replace(/[^a-zA-Z0-9_-]+/g, '_');
  const typeSuffix = options?.taskTypeId
    ? `-${options.taskTypeId.replace(/[^a-zA-Z0-9_-]+/g, '_')}`
    : '';
  return `tasks-${safeId}${typeSuffix}-${stamp}.${format}`;
}
